/**
 * «منشی هوشمند» — what came back, as notifications (PM-0517; the clinic panel's
 * SecretaryNotices, for the doctor's results).
 *
 * A notification says what was understood and waits until it is opened or
 * dismissed: the command may already have cost پارسه, so — unlike a toast — it
 * never leaves on a timer. At most three show; the rest wait behind «+N». Stacked
 * beside the record button at the foot of the sidebar, newest nearest the button.
 */
import React from 'react';
import {
  CalendarCheck, CalendarClock, CircleHelp, FolderOpen, Hospital, ListChecks, RotateCcw, TriangleAlert, Users, Wallet, X,
} from 'lucide-react';
import { C, soft } from '@parsina/framework/clinical/src/constants/theme';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

import { resultPatient, slotsSpanClinics } from './model';
import { useSecretaryStrings } from './secretaryStrings';

const TONES = { ok: () => C.teal, pick: () => C.navy, info: () => C.navy, warn: () => C.amber };
const MAX_SHOWN = 3;

/** What one result says in its notification. */
export function describeNotice(item, s, tr) {
  const { formatDate, formatTime, formatNumber, formatDigits } = tr;
  const r = item.result;
  if (item.error === 'credit') return { tone: 'warn', Icon: Wallet, title: s('secretary.notice.noCredit'), line: '' };
  if (item.error === 'failed' || !r) return { tone: 'warn', Icon: RotateCcw, title: s('secretary.notice.failed'), line: '' };
  const patient = resultPatient(r);
  const name = patient?.name || r.heardName || r.detail || '';
  switch (r.outcome) {
    case 'PROPOSAL': {
      const b = r.booking;
      const first = b?.slots?.[0];
      const line = first ? s(slotsSpanClinics(b) || b.clinicAssumed ? 'secretary.notice.slotLineClinic' : 'secretary.notice.slotLine', {
        day: formatDate(first.dateTime, 'weekdayDayMonth'), time: formatTime(first.dateTime), clinic: first.clinicName || '',
      }) : '';
      return {
        tone: 'ok',
        Icon: r.kind === 'follow_up' ? CalendarClock : CalendarCheck,
        title: s(r.kind === 'follow_up' ? 'secretary.notice.followUpReady' : 'secretary.notice.bookReady', { name: b?.patient?.name || name }),
        line,
      };
    }
    case 'NO_SLOT':
      return { tone: 'warn', Icon: CalendarClock, title: s('secretary.notice.noSlot'), line: [name, r.booking?.clinic?.name].filter(Boolean).join(' · ') };
    case 'CHOOSE_PATIENT':
      return {
        tone: 'pick', Icon: Users, title: s('secretary.notice.choosePatient', { name: r.heardName || '' }),
        line: s('secretary.notice.candidates', { count: formatNumber(r.patients?.length || 0) }),
      };
    case 'NOT_FOUND':
      return { tone: 'warn', Icon: CircleHelp, title: s('secretary.notice.notFound', { name: r.detail || r.heardName || '' }), line: '' };
    case 'NAVIGATE':
      return {
        tone: 'ok', Icon: FolderOpen, title: s('secretary.notice.openRecord', { name }),
        line: patient?.fileNumber ? s('secretary.window.file', { file: formatDigits(patient.fileNumber) }) : '',
      };
    case 'ANSWER':
      if (r.daySummary) {
        const d = r.daySummary;
        return {
          tone: 'info', Icon: ListChecks, title: s('secretary.notice.daySummary', { total: formatNumber(d.total) }),
          line: s('secretary.window.dayCounts', {
            completed: formatNumber(d.completed), arrived: formatNumber(d.arrived), scheduled: formatNumber(d.scheduled),
          }),
        };
      }
      break;
    case 'NO_CLINIC':
      return { tone: 'warn', Icon: Hospital, title: s(r.reason === 'read_only' ? 'secretary.window.readOnlyTitle' : 'secretary.notice.noClinic'), line: '' };
    case 'OUT_OF_SCOPE':
      return { tone: 'warn', Icon: TriangleAlert, title: s('secretary.notice.outOfScope'), line: '' };
    case 'UNAVAILABLE':
      return { tone: 'warn', Icon: CircleHelp, title: s('secretary.notice.unavailable'), line: '' };
    default:
      break;
  }
  return { tone: 'warn', Icon: CircleHelp, title: s('secretary.notice.unknown'), line: '' };
}

function Notice({ item, newest, onOpen, onDismiss, s, tr }) {
  const d = describeNotice(item, s, tr);
  const tone = TONES[d.tone]?.() || C.navy;
  const { Icon } = d;
  return (
    <div
      role="group"
      aria-label={d.title}
      data-testid={`secretary-notice-${item.id}`}
      data-tone={d.tone}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 4, padding: 4, borderRadius: 14,
        background: C.card, border: `1px solid ${C.border}`,
        boxShadow: '0 10px 28px rgba(13, 27, 38, 0.16), 0 1px 3px rgba(13, 27, 38, 0.10)',
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-keyshortcuts={newest ? 'Shift+F1' : undefined}
        data-testid="secretary-notice-open"
        style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 8px 8px 4px',
          border: 0, borderRadius: 11, background: 'transparent', color: C.textPrimary, textAlign: 'start',
          fontFamily: 'inherit', cursor: 'pointer',
        }}
      >
        <span aria-hidden="true" style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 11, display: 'grid', placeItems: 'center', background: soft(tone, 0.14), color: tone }}>
          <Icon size={18} />
        </span>
        <span style={{ flex: 1, minWidth: 0, display: 'grid', gap: 1 }}>
          <span dir="auto" style={{ fontSize: '0.875rem', fontWeight: 800, lineHeight: 1.45 }} data-testid="secretary-notice-title">{d.title}</span>
          {d.line ? <span dir="auto" style={{ fontSize: '0.78125rem', color: C.textSecondary }}>{d.line}</span> : null}
          {item.text ? (
            <span dir="auto" style={{ fontSize: '0.75rem', color: C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {s('secretary.notice.heard', { text: item.text })}
            </span>
          ) : null}
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 5 }}>
            <span style={{ fontSize: '0.78125rem', fontWeight: 800, color: C.navy }}>{s('secretary.notice.open')}</span>
            <span style={{ marginInlineStart: 'auto', fontSize: '0.71875rem', color: C.textMuted }}>{tr.formatTime(item.createdAt)}</span>
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={s('secretary.notice.dismiss')}
        title={s('secretary.notice.dismiss')}
        data-testid="secretary-notice-dismiss"
        style={{
          width: 28, height: 28, flexShrink: 0, marginTop: 4, border: 0, borderRadius: 8, background: 'transparent',
          color: C.textMuted, display: 'grid', placeItems: 'center', cursor: 'pointer',
        }}
      >
        <X size={15} aria-hidden="true" />
      </button>
    </div>
  );
}

export default function SecretaryNotices({ items, onOpen, onDismiss, sidebarWidth = 0 }) {
  const tr = useTranslation();
  const s = useSecretaryStrings();
  const [expanded, setExpanded] = React.useState(false);
  React.useEffect(() => { if (items.length <= MAX_SHOWN) setExpanded(false); }, [items.length]);
  if (!items.length) return null;
  const newestFirst = [...items].reverse();
  const shown = expanded ? newestFirst : newestFirst.slice(0, MAX_SHOWN);
  const rest = newestFirst.length - shown.length;
  return (
    <section
      aria-label={s('secretary.notice.listLabel')}
      data-testid="secretary-notices"
      style={{
        position: 'fixed', bottom: 16, insetInlineStart: sidebarWidth + 16, width: 360, zIndex: 60,
        display: 'flex', flexDirection: 'column', gap: 10,
        maxHeight: 'calc(100dvh - 32px)', overflowY: expanded ? 'auto' : 'visible',
      }}
    >
      {rest > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid="secretary-notices-more"
          style={{
            alignSelf: 'center', border: `1px solid ${C.border}`, borderRadius: 999, background: C.card, color: C.textSecondary,
            fontFamily: 'inherit', fontSize: '0.75rem', fontWeight: 700, padding: '2px 12px', cursor: 'pointer',
          }}
        >
          {s('secretary.notice.more', { count: tr.formatNumber(rest) })}
        </button>
      ) : null}
      {/* Newest nearest the button: last in the column. */}
      {[...shown].reverse().map((item) => (
        <Notice
          key={item.id}
          item={item}
          newest={item === newestFirst[0]}
          onOpen={() => onOpen(item)}
          onDismiss={() => onDismiss(item.id)}
          s={s}
          tr={tr}
        />
      ))}
    </section>
  );
}
